import { useMemo, useState } from "react";
import { Package, BadgeDollarSign, Sparkles, CalendarRange } from "lucide-react";
import { Card } from "@/components/ui/card";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthsBack = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return ym(d); };

export function StatCard({ label, value, icon: Icon, hint, testid }) {
  return (
    <Card className="border-slate-200 shadow-sm p-3" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
          {Icon && <Icon className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
          <div className="text-lg font-semibold text-slate-800 truncate">{value}</div>
          {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}

const PRESETS = [
  { id: "last", label: "Last month", range: () => [monthsBack(1), monthsBack(1)] },
  { id: "3m", label: "Last 3 months", range: () => [monthsBack(3), monthsBack(1)] },
  { id: "12m", label: "Last 12 months", range: () => [monthsBack(12), monthsBack(1)] },
  { id: "ytd", label: "Year to date", range: () => [`${new Date().getFullYear()}-01`, ym(new Date())] },
  { id: "all", label: "All time", range: (periods) => [periods[0] || monthsBack(1), periods[periods.length - 1] || monthsBack(1)] },
];

const MonthSelect = ({ value, options, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700">
    {options.map((p) => <option key={p} value={p}>{p}</option>)}
  </select>
);

export function PartnerRangeStats({ timeseries, deals, paused }) {
  const [[from, to], setRange] = useState(() => PRESETS[0].range());
  const periods = useMemo(() => timeseries.map((t) => t.period_label).filter(Boolean), [timeseries]);
  const options = useMemo(() => [...new Set([...periods, from, to])].sort(), [periods, from, to]);

  const stats = useMemo(() => {
    const inRange = (p) => p >= from && p <= to;
    const ts = timeseries.filter((t) => inRange(t.period_label));
    const sum = (k) => ts.reduce((s, t) => s + (Number(t[k]) || 0), 0);
    const dealCount = deals.length ? deals.filter((d) => inRange(d.period_label || "")).length : sum("deals");
    return { deals: dealCount, revenue: sum("revenue"), commission: sum("commission"),
             earned: sum("payable_commission"), paid: sum("paid") };
  }, [timeseries, deals, from, to]);

  const label = from === to ? from : `${from} → ${to}`;
  const setFrom = (v) => setRange(([, t]) => [v, v > t ? v : t]);
  const setTo = (v) => setRange(([f]) => [v < f ? v : f, v]);

  return (
    <div className="mb-4 space-y-2" data-testid="partner-range-stats">
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="partner-range-bar">
        <CalendarRange className="h-4 w-4 text-slate-400" />
        <MonthSelect value={from} options={options} onChange={setFrom} testid="partner-range-from" />
        <span className="text-slate-400">to</span>
        <MonthSelect value={to} options={options} onChange={setTo} testid="partner-range-to" />
        <div className="ml-1 flex flex-wrap gap-1">
          {PRESETS.map((p) => {
            const [pf, pt] = p.range(periods);
            const active = pf === from && pt === to;
            return (
              <button key={p.id} type="button" onClick={() => setRange([pf, pt])} data-testid={`partner-range-preset-${p.id}`}
                      className={`rounded-full border px-2.5 py-1 font-medium transition-colors ${active
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:text-slate-700"}`}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label={`Deals (${label})`} value={stats.deals.toLocaleString()} icon={Package} testid="partner-stat-deals" />
        <StatCard label={`Revenue (${label})`} value={money(stats.revenue)} icon={BadgeDollarSign} testid="partner-stat-revenue" />
        <StatCard label={`Commission (${label})`} value={money(stats.commission)} icon={Sparkles} testid="partner-stat-commission" />
        <StatCard label={`Payable commission (${label})`} value={money(stats.earned - stats.paid)} icon={Sparkles}
                  testid="partner-stat-payable"
                  hint={stats.paid || stats.earned
                    ? `${money(stats.paid)} paid · ${money(stats.earned)} earned payable`
                    : (paused ? "Only unpaused deals count" : "All deals count")} />
      </div>
    </div>
  );
}
